import React, { useCallback } from 'react';
import { format } from 'date-fns';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel';
import {
  type DueDatePreset,
  type DateRange,
  type RequestTypeFilter,
  type AssigneeFilterOption,
  workStateOptions,
  dueDateOptions,
  requestTypeOptions,
} from '../../Panels/BrowserPanel/components/BrowserToolbar/components/FilterModal';
import type { WorkState } from '../../Panels/BrowserPanel/components/RequestCard';
import { IconFilter } from 'components/Icons';
import KSButton from '../../Common/KSButton';
import './MobileAdvancedFiltersPanel.scss';

export interface MobileAdvancedFiltersPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when panel closes */
  onClose: () => void;

  // Status (WorkState) filter
  workStateFilter: Set<WorkState>;
  onWorkStateFilterChange: (states: Set<WorkState>) => void;
  workStateCounts: Record<WorkState, number>;

  // Due date filter
  dueDateFilter: DueDatePreset;
  onDueDateFilterChange: (preset: DueDatePreset) => void;

  // Date range filter
  dateRangeFilter: DateRange;
  onDateRangeFilterChange: (range: DateRange) => void;

  // Request type filter
  requestTypeFilter: Set<RequestTypeFilter>;
  onRequestTypeFilterChange: (types: Set<RequestTypeFilter>) => void;
  requestTypeCounts?: Record<RequestTypeFilter, number>;

  // Assignee filter
  assigneeFilter: string | null;
  onAssigneeFilterChange: (userId: string | null) => void;
  assigneeOptions: AssigneeFilterOption[];

  // Actions
  onClearAll: () => void;
}

/**
 * MobileAdvancedFiltersPanel - Mobile-optimized advanced filters
 *
 * A slide-up panel version of FilterModalContent, redesigned for mobile with
 * touch-friendly controls and a vertical single-column layout.
 */
export const MobileAdvancedFiltersPanel: React.FC<MobileAdvancedFiltersPanelProps> = ({
  isOpen,
  onClose,
  workStateFilter,
  onWorkStateFilterChange,
  workStateCounts,
  dueDateFilter,
  onDueDateFilterChange,
  dateRangeFilter,
  onDateRangeFilterChange,
  requestTypeFilter,
  onRequestTypeFilterChange,
  requestTypeCounts,
  assigneeFilter,
  onAssigneeFilterChange,
  assigneeOptions,
  onClearAll,
}) => {
  const prefix = prefixClass('ksq2-mobile-advanced-filters');

  // Toggle a work state in the filter set
  const toggleWorkState = useCallback(
    (state: WorkState) => {
      const newSet = new Set(workStateFilter);
      if (newSet.has(state)) {
        newSet.delete(state);
      } else {
        newSet.add(state);
      }
      onWorkStateFilterChange(newSet);
    },
    [workStateFilter, onWorkStateFilterChange]
  );

  // Toggle a request type in the filter set
  const toggleRequestType = useCallback(
    (type: RequestTypeFilter) => {
      const newSet = new Set(requestTypeFilter);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      onRequestTypeFilterChange(newSet);
    },
    [requestTypeFilter, onRequestTypeFilterChange]
  );

  // Handle date range changes
  const handleFromDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const fromDate = value ? new Date(value + 'T00:00:00') : null;
      onDateRangeFilterChange({ ...dateRangeFilter, from: fromDate });
    },
    [dateRangeFilter, onDateRangeFilterChange]
  );

  const handleToDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const toDate = value ? new Date(value + 'T23:59:59') : null;
      onDateRangeFilterChange({ ...dateRangeFilter, to: toDate });
    },
    [dateRangeFilter, onDateRangeFilterChange]
  );

  // Filter out work state options with zero count
  const availableWorkStateOptions = workStateOptions.filter(option => workStateCounts[option.value] > 0);

  // Check if any filter is active
  const hasActiveFilters =
    workStateFilter.size > 0 ||
    dueDateFilter !== null ||
    dateRangeFilter.from !== null ||
    dateRangeFilter.to !== null ||
    requestTypeFilter.size > 0 ||
    assigneeFilter !== null;

  // Format date for input value
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  // Handle apply - just close the panel (filters are applied immediately)
  const handleApply = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle clear and close
  const handleClearAll = useCallback(() => {
    onClearAll();
  }, [onClearAll]);

  return (
    <MobileSlideUpPanel isOpen={isOpen} onClose={onClose} title="Filters" titleIcon={<IconFilter />} fullSizeOnOpen>
      <div className={prefix()}>
        {/* Scrollable content area */}
        <div className={prefix('__content')}>
          {/* Status Filter */}
          {availableWorkStateOptions.length > 0 && (
            <div className={prefix('__section')}>
              <div className={prefix('__section-title')}>Status</div>
              <div className={prefix('__chips')}>
                {availableWorkStateOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={classNames(prefix('__chip'), {
                      [prefix('__chip--active')]: workStateFilter.has(option.value),
                    })}
                    onClick={() => toggleWorkState(option.value)}
                  >
                    <span className={prefix('__chip-icon')}>{option.icon}</span>
                    <span className={prefix('__chip-label')}>{option.label}</span>
                    <span className={prefix('__chip-count')}>{workStateCounts[option.value]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Due Date Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Due Date</div>
            <div className={prefix('__chips')}>
              {dueDateOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={classNames(prefix('__chip'), {
                    [prefix('__chip--active')]: dueDateFilter === option.value,
                  })}
                  onClick={() => onDueDateFilterChange(dueDateFilter === option.value ? null : option.value)}
                >
                  <span className={prefix('__chip-label')}>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Date Range</div>
            <div className={prefix('__date-range')}>
              <div className={prefix('__date-input-wrapper')}>
                <label className={prefix('__date-label')}>From</label>
                <input
                  type="date"
                  className={prefix('__date-input')}
                  value={formatDateForInput(dateRangeFilter.from)}
                  onChange={handleFromDateChange}
                />
              </div>
              <div className={prefix('__date-input-wrapper')}>
                <label className={prefix('__date-label')}>To</label>
                <input
                  type="date"
                  className={prefix('__date-input')}
                  value={formatDateForInput(dateRangeFilter.to)}
                  onChange={handleToDateChange}
                  min={dateRangeFilter.from ? formatDateForInput(dateRangeFilter.from) : undefined}
                />
              </div>
            </div>
          </div>

          {/* Request Type Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Request Type</div>
            <div className={prefix('__chips')}>
              {requestTypeOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={classNames(prefix('__chip'), {
                    [prefix('__chip--active')]: requestTypeFilter.has(option.value),
                  })}
                  onClick={() => toggleRequestType(option.value)}
                >
                  <span className={prefix('__chip-label')}>{option.label}</span>
                  {requestTypeCounts && (
                    <span className={prefix('__chip-count')}>{requestTypeCounts[option.value] || 0}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Assignee Filter */}
          {assigneeOptions.length > 0 && (
            <div className={prefix('__section')}>
              <div className={prefix('__section-title')}>Assignee</div>
              <div className={prefix('__select-wrapper')}>
                <select
                  className={prefix('__select')}
                  value={assigneeFilter || ''}
                  onChange={e => onAssigneeFilterChange(e.target.value || null)}
                >
                  <option value="">Any</option>
                  {assigneeOptions.map(option => (
                    <option key={option.userId} value={option.userId}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Fixed actions footer */}
        <div className={prefix('__actions')}>
          {hasActiveFilters && <KSButton variant="secondary" label="Clear All" onClick={handleClearAll} />}
          <KSButton variant="primary" label="Apply" onClick={handleApply} />
        </div>
      </div>
    </MobileSlideUpPanel>
  );
};

export default MobileAdvancedFiltersPanel;
